import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live');
const API = `${BACKEND_URL}/api`;

const Star = ({ filled }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? '#D4AF37' : 'none'} stroke="#D4AF37" strokeWidth="1.5">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const TestimonialsAdminTab = ({ adminKey }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${adminKey}` };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/testimonials?status=${filter}`, { headers });
      setItems(r.data.items || []);
      setCounts(r.data.counts || { pending: 0, approved: 0 });
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [filter, adminKey]); // eslint-disable-line

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const approve = async (id) => {
    await axios.patch(`${API}/admin/testimonials/${id}`, { status: "approved" }, { headers });
    fetchItems();
  };

  const reject = async (id) => {
    await axios.patch(`${API}/admin/testimonials/${id}`, { status: "pending" }, { headers });
    fetchItems();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce témoignage ?")) return;
    await axios.delete(`${API}/admin/testimonials/${id}`, { headers });
    fetchItems();
  };

  const filterBtn = (key, label, badge) => (
    <button
      key={key}
      data-testid={`testimonial-filter-${key}`}
      onClick={() => setFilter(key)}
      style={{
        padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
        fontFamily: "'Georgia', serif", fontSize: 13, transition: 'all 0.2s',
        border: filter === key ? '1px solid #D4AF37' : '1px solid rgba(212,175,55,0.18)',
        background: filter === key ? 'rgba(212,175,55,0.1)' : 'transparent',
        color: filter === key ? '#D4AF37' : 'rgba(212,175,55,0.6)',
      }}
    >
      {label}{badge != null ? ` (${badge})` : ''}
    </button>
  );

  return (
    <div data-testid="admin-testimonials-section">
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
        {filterBtn('all', 'Tous')}
        {filterBtn('pending', 'En attente', counts.pending)}
        {filterBtn('approved', 'Publiés', counts.approved)}
      </div>

      {loading && <p style={{ color: 'rgba(212,175,55,0.5)', fontFamily: "'Georgia', serif", textAlign: 'center' }}>Chargement…</p>}

      {!loading && items.length === 0 && (
        <p style={{ color: 'rgba(247,230,189,0.5)', fontFamily: "'Georgia', serif", fontStyle: 'italic', textAlign: 'center', padding: 40 }}>
          Aucun témoignage dans cette catégorie.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {items.map(item => (
          <article key={item.id} data-testid={`admin-testimonial-${item.id}`} style={{
            padding: '20px 22px', borderRadius: 10,
            background: 'rgba(212,175,55,0.04)',
            border: `1px solid ${item.status === 'pending' ? 'rgba(212,175,55,0.45)' : 'rgba(212,175,55,0.15)'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', fontWeight: 500 }}>{item.name}</span>
                  {item.location && <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(247,230,189,0.6)', fontStyle: 'italic' }}>{item.location}</span>}
                  <span style={{ display: 'flex', gap: 2 }}>
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} filled={i <= item.rating} />)}
                  </span>
                  <span style={{
                    fontFamily: "'Georgia', serif", fontSize: 10, padding: '3px 10px', borderRadius: 12, letterSpacing: 0.5,
                    background: item.status === 'pending' ? 'rgba(212,175,55,0.18)' : 'rgba(34,139,34,0.15)',
                    color: item.status === 'pending' ? '#D4AF37' : '#7BC97B',
                    border: `1px solid ${item.status === 'pending' ? 'rgba(212,175,55,0.4)' : 'rgba(123,201,123,0.3)'}`,
                  }}>{item.status === 'pending' ? 'EN ATTENTE' : 'PUBLIÉ'}</span>
                </div>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#F7E6BD', lineHeight: 1.7, fontStyle: 'italic', margin: '8px 0 0' }}>
                  « {item.text} »
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {item.status === 'pending' ? (
                  <button data-testid={`testimonial-approve-${item.id}`} onClick={() => approve(item.id)} style={{
                    padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                    background: 'linear-gradient(135deg, #D6BB6A 0%, #C9A632 30%, #A8830C 65%, #6B4F0A 100%)',
                    color: '#000', fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 600, border: 'none', letterSpacing: 0.3,
                  }}>Approuver</button>
                ) : (
                  <button data-testid={`testimonial-unpublish-${item.id}`} onClick={() => reject(item.id)} style={{
                    padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                    background: 'transparent', color: 'rgba(212,175,55,0.6)',
                    border: '1px solid rgba(212,175,55,0.25)', fontFamily: "'Georgia', serif", fontSize: 12,
                  }}>Dépublier</button>
                )}
                <button data-testid={`testimonial-delete-${item.id}`} onClick={() => remove(item.id)} style={{
                  padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                  background: 'transparent', color: 'rgba(220,80,80,0.7)',
                  border: '1px solid rgba(220,80,80,0.3)', fontFamily: "'Georgia', serif", fontSize: 12,
                }}>Supprimer</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default TestimonialsAdminTab;
